import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { QueryState } from '../components/page';
import { usePracticeAgents } from '../api/hooks';
import type { PracticeAgentDevice } from '../api/types';
import {
  ConnectionBadge,
  PracticeAgentsHeader,
  UNAVAILABLE,
  agoText,
  control,
  frozenText,
  lastImportText,
  practiceLoadError,
  selfCheckProblems,
  updateText,
} from './practice-agents';

type StateFilter = 'current' | 'online' | 'offline' | 'revoked' | 'all';
type ChannelFilter = 'all' | 'stable' | 'pilot';

const STATE_FILTERS: { value: StateFilter; label: string }[] = [
  { value: 'current', label: 'Online and offline' },
  { value: 'online', label: 'Online' },
  { value: 'offline', label: 'Offline' },
  { value: 'revoked', label: 'Revoked' },
  { value: 'all', label: 'All, with revoked' },
];

function matchesState(device: PracticeAgentDevice, filter: StateFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'revoked') return device.connection === 'revoked';
  if (filter === 'online') return device.connection === 'connected';
  if (filter === 'offline') return device.connection === 'offline';
  return device.connection !== 'revoked';
}

export function PracticeAgentsPage() {
  const [state, setState] = useState<StateFilter>('current');
  const [channel, setChannel] = useState<ChannelFilter>('all');
  const devices = usePracticeAgents(state === 'revoked' || state === 'all');
  const rows = (devices.data ?? []).filter(
    device => matchesState(device, state) && (channel === 'all' || device.channel === channel)
  );
  const filtered = state !== 'current' || channel !== 'all';

  return (
    <div>
      <PracticeAgentsHeader />
      <QueryState isLoading={devices.isLoading} error={practiceLoadError(devices.error)}>
        {devices.data === null ? (
          <p role="status" className="text-sm text-muted-foreground">
            {UNAVAILABLE}
          </p>
        ) : (
          <>
            <div className="mb-4 flex flex-wrap items-end gap-3 rounded-3xl border bg-card p-4">
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                State
                <select
                  aria-label="State"
                  className={control}
                  value={state}
                  onChange={event => setState(event.target.value as StateFilter)}
                >
                  {STATE_FILTERS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-xs text-muted-foreground">
                Channel
                <select
                  aria-label="Channel"
                  className={control}
                  value={channel}
                  onChange={event => setChannel(event.target.value as ChannelFilter)}
                >
                  <option value="all">All channels</option>
                  <option value="stable">Stable</option>
                  <option value="pilot">Pilot</option>
                </select>
              </label>
            </div>
            <div className="overflow-x-auto rounded-3xl border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Practice</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Versions</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Last import</TableHead>
                    <TableHead>Self-check</TableHead>
                    <TableHead>Updates and errors</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(device => (
                    <DeviceRow key={device.id} device={device} />
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="h-28 text-center text-muted-foreground">
                        {filtered ? (
                          'No practice computers match these filters.'
                        ) : (
                          <>
                            No practice computers yet. Create a setup link for a Practice in{' '}
                            <Link to="/clinical-trials" className="underline underline-offset-4">
                              Clinical Changes
                            </Link>
                            .
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}

function DeviceRow({ device }: { device: PracticeAgentDevice }) {
  const heartbeat = device.lastHeartbeat;
  const problems = selfCheckProblems(heartbeat);
  const update = updateText(heartbeat);
  const frozen = frozenText(heartbeat);
  return (
    <TableRow>
      <TableCell className="min-w-[14rem]">
        <Link
          to="/practice-agents/$deviceId"
          params={{ deviceId: device.id }}
          className="font-medium underline-offset-4 hover:underline"
        >
          {device.practiceName ?? 'Unnamed Practice'}
        </Link>
        <div className="text-xs text-muted-foreground">
          {[device.tenantName, device.teamName].filter(Boolean).join(' · ') || 'No tenant'}
        </div>
      </TableCell>
      <TableCell className="min-w-[9rem]">
        <ConnectionBadge connection={device.connection} />
        <div className="mt-1 text-xs text-muted-foreground">
          Last seen {agoText(device.lastSeenAt)}
        </div>
      </TableCell>
      <TableCell className="min-w-[9rem] text-xs">
        <div>App {device.appVersion}</div>
        <div>
          Bundle {device.bundleVersion ?? 'none'}
          {device.pinnedBundleVersion && ` (pinned ${device.pinnedBundleVersion})`}
        </div>
      </TableCell>
      <TableCell>
        <div className="capitalize">{device.channel}</div>
        {frozen.map(text => (
          <Badge key={text} variant="outline" className="mt-1">
            {text}
          </Badge>
        ))}
      </TableCell>
      <TableCell className="min-w-[10rem] text-xs">
        <span className={heartbeat?.lastImport?.status === 'failed' ? 'text-destructive' : ''}>
          {lastImportText(heartbeat)}
        </span>
      </TableCell>
      <TableCell className="min-w-[12rem] text-xs">
        {!heartbeat?.selfCheck ? (
          <span className="text-muted-foreground">No report yet</span>
        ) : problems.length === 0 ? (
          <span className="text-muted-foreground">No problems</span>
        ) : (
          <ul className="space-y-0.5 text-destructive">
            {problems.map(problem => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}
      </TableCell>
      <TableCell className="min-w-[10rem] text-xs">
        {update && <div>{update}</div>}
        {heartbeat?.lastErrorClass && (
          <div className="text-destructive">Last error {heartbeat.lastErrorClass}</div>
        )}
        {!update && !heartbeat?.lastErrorClass && (
          <span className="text-muted-foreground">None</span>
        )}
      </TableCell>
    </TableRow>
  );
}
